import React, { Component } from 'react';
import { connect } from 'react-redux';
import PdfUriViewer from '../documentViewers/pdf/PdfUriViewer';
import XmlUriViewer from '../documentViewers/xml/XmlUriViewer';
import FixedFormatUriViewer from '../documentViewers/fixedformat/FixedFormatUriViewer';
import FileSaveAs from '../downloaders/FileSaveAs';
import FILING_CONTROLLER from '../../controllers/filing';
import ApproveFiling from './ApproveFiling';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import MTDSubmitFiling from './MTDSubmitFiling';
import DESubmitFiling from './DESubmitFiling';
import NLSubmitFiling from './NLSubmitFiling';
import NLSubmissionFilingV2 from './NLSubmissionFilingV2';
import Logger, { LOG_LEVELS, ALL_MESSAGES } from '../../utils/logger';
import { withFullStoryExclude } from '../../utils/fullStoryHelper';
import SaveFilingToWorkflow from './SaveFilingToWorkflow';
import ConfirmationModal from '../modals/ConfirmationModal';
import ApplicationMessagesWrapper from '../inAppMessages/ApplicationMessagesWrapper';
import ENUMS, { CountryCodeEnum, FilingTypeEnum, FeatureFlagEnum } from '../../utils/enums';
import FilingHelper from './filingHelper';
import { isAutoSubmissionEnabled } from '../../utils/autoSubmissionConfig';
import { canInitiateDirectSubmissionGated } from '../../utils/directSubmissionAccess';
import LANGUAGES_CONTROLLER from '../../controllers/languages';
import ReadOnlyWrapper from '../authorization/ReadOnlyWrapper';
import CLIENT from '../../utils/restClient';
import BusySpinner from '../spinners/BusySpinner';
import getClientConfig from '../../utils/getClientConfig';

const log = new Logger('FILING_PREVIEW', LOG_LEVELS.DEBUG);
const DE_VAT_FORM_NAME = 'ERIC submission';
const DE_VAT_FILE_NAMES = ['VAT Return DE', 'VAT_DE', 'VAT_Corr_DE'];
const NL_VAT_FILE_NAMES = ['VAT Return NL', 'VAT_NL', 'VAT_Corr_NL'];
const NL_ECL_FILE_NAMES = ['EC Listing NL', 'ECL_NL'];
const XML_FORM_NAME = 'XML';
const DE_ECL_FILE_NAMES = ['EC Listing DE', 'ECL_DE'];
const GB_FORM_NAME = 'FixedFormat';
const GB_FILE_NAMES = ['VAT Return GB', 'VAT_GB'];
const PERIOD_YEAR = 'Yearly';

// Hungary (HU) specific: maximum XML size (in bytes) allowed for in-browser preview.
// Files larger than this are download-only to avoid browser freezes/crashes.
// 0.50 MB = 0.5 * 1024 * 1024 bytes.
const HU_XML_PREVIEW_MAX_BYTES = 0.5 * 1024 * 1024;

// Helper to build correction period string from selectedCorrectionPeriod
const buildCorrectionPeriod = (selectedCorrectionPeriod) => {
  if (!selectedCorrectionPeriod) return null;
  
  const year = selectedCorrectionPeriod.year || selectedCorrectionPeriod.correctionYear;
  const period = selectedCorrectionPeriod.period || selectedCorrectionPeriod.correctionPeriod;
  
  return (year && period) ? `${year}-${period}` : null;
};

/** XML size check result: null = not applicable, pending = HEAD in progress, small = show preview, large = message + download only, fallback = HEAD failed -> show preview */
const XML_SIZE_CHECK = { PENDING: 'pending', SMALL: 'small', LARGE: 'large', FALLBACK: 'fallback' };

class FilingPreview extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      isApprovalModalOpen: false,
      xmlSizeCheckStatus: null,
      isInitiating: false,
    };
  }

  isXmlVatReturn(props = this.props) {
    const { documentType, filingTypeId } = props;
    const isXml = documentType === 'XML' || documentType === 'text/xml' || documentType === 'application/xml' ||
      (typeof documentType === 'string' && documentType.trim().toUpperCase() === 'XML');
    const isVatReturn = filingTypeId === FilingTypeEnum.VATRETURN ||
      (typeof filingTypeId === 'string' && filingTypeId.trim() === 'VATReturn');
    return isXml && isVatReturn;
  }

  isXmlDocument(props = this.props) {
    const { documentType } = props;
    return documentType === 'XML' || documentType === 'text/xml' || documentType === 'application/xml' ||
      (typeof documentType === 'string' && documentType.trim().toUpperCase() === 'XML');
  }

  /**
   * HU-specific guardrail applies only to VAT Return XML for Hungary (HU).
   * Uses existing enum-based country logic (CountryCodeEnum) used elsewhere in the UI.
   */
  isHuVatReturnXml(props = this.props) {
    if (!this.isXmlVatReturn(props)) return false;
    const countryEnum = CountryCodeEnum.enumValueOf((props.countryCode || '').toUpperCase());
    return countryEnum === CountryCodeEnum.HU;
  }

  checkXmlSize() {
    const { documentUri } = this.props;
    // Guardrail must run ONLY for HU VAT Return XML. All other countries remain unchanged.
    if (!this.isHuVatReturnXml()) return;
    if (!documentUri || typeof documentUri !== 'string') {
      // No URI to HEAD; fall back to existing behavior (preview as today)
      this.setState({ xmlSizeCheckStatus: XML_SIZE_CHECK.FALLBACK });
      return;
    }
    this.setState({ xmlSizeCheckStatus: XML_SIZE_CHECK.PENDING });
    CLIENT.headFilingDocument(documentUri)
      .then((response) => {
        if (!response.ok) {
          log.warn('HEAD for XML size check failed or non-2xx', response.status);
          this.setState({ xmlSizeCheckStatus: XML_SIZE_CHECK.FALLBACK });
          return;
        }
        const contentLengthHeader = response.headers.get('Content-Length') || response.headers.get('content-length');
        let contentLength = null;
        if (contentLengthHeader) {
          const parsed = Number(contentLengthHeader);
          contentLength = Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
        }
        if (contentLength == null) {
          log.warn('HEAD for XML size check: Content-Length missing or invalid');
          this.setState({ xmlSizeCheckStatus: XML_SIZE_CHECK.FALLBACK });
          return;
        }
        if (contentLength <= HU_XML_PREVIEW_MAX_BYTES) {
          this.setState({ xmlSizeCheckStatus: XML_SIZE_CHECK.SMALL });
        } else {
          this.setState({ xmlSizeCheckStatus: XML_SIZE_CHECK.LARGE });
        }
      })
      .catch((err) => {
        log.warn('HEAD for XML size check failed', err);
        this.setState({ xmlSizeCheckStatus: XML_SIZE_CHECK.FALLBACK });
      });
  }

  onDownloadLargeXml = () => {
    return CLIENT.getFilingDocumentAsBlob('Blob', this.props.documentUri).then((blob) => ({
      content: blob,
      ext: this.props.documentExtension || 'xml',
    }));
  };

  messageIdsToIgnoreInThisComponent = () => {
    const deErrors = [
      ALL_MESSAGES.SUBMIT_FILING_DE_BLANK_FILE,
      ALL_MESSAGES.SUBMIT_FILING_DE_BLANK_PIN,
      ALL_MESSAGES.SUBMIT_FILING_DE_FILE_UPLOAD_SUCCESS,
      ALL_MESSAGES.SUBMIT_FILING_DE_MAX_PIN,
      ALL_MESSAGES.VAT_RETURN_SUBMISSION_FAILED,
    ];

    const gbErrors = [
      ALL_MESSAGES.VAT_RETURN_SUBMISSION_AUTH_FAILED,
    ];

    const nlErrors = [
      ALL_MESSAGES.MAX_LENGTH_PASSWORD,
      ALL_MESSAGES.SUBMIT_FILING_NL_ALREADY_SUCCESS,
      ALL_MESSAGES.SUBMIT_FILING_NL_BLANK_PASSWORD,
      ALL_MESSAGES.SUBMIT_FILING_NL_BLANK_USERNAME,
      ALL_MESSAGES.SUBMIT_FILING_NL_CURRENT_ERROR_STATES,
      ALL_MESSAGES.SUBMIT_FILING_NL_CURRENT_PENDING_STATES,
      ALL_MESSAGES.SUBMIT_FILING_NL_ERROR,
      ALL_MESSAGES.SUBMIT_FILING_NL_PENDING,
      ALL_MESSAGES.SUBMIT_FILING_NL_SUCCESS,
    ];

    const manualDocumentMessages = [
      ALL_MESSAGES.BLANK_BOX_NAME, 
      ALL_MESSAGES.BLANK_BOX_VALUE, 
      ALL_MESSAGES.BLANK_CURRENCY, 
      ALL_MESSAGES.BLANK_TRANSACTION_DATE, 
      ALL_MESSAGES.BLANK_INVOICE_DATE, 
      ALL_MESSAGES.VAT_BOX_REQUIRED,
      ALL_MESSAGES.CREATE_MANUAL_DOCUMENT_SUCCESS,
    ]
    return deErrors.concat(nlErrors).concat(gbErrors).concat(manualDocumentMessages);
  };

  openApprovalModal = () => {
    this.setState({ isApprovalModalOpen: true });
  };

  closeApprovalModal = () => {
    this.setState({ isApprovalModalOpen: false });
  };

  getSaveToWorkflowButton = () => {
    const shouldHideButton = !this.props.canUserSaveToWorkflow || 
                             this.props.filingTypeId === FilingTypeEnum.REVERSECHARGELISTING;
    
    if (shouldHideButton) return null;

    return <SaveFilingToWorkflow onSaveToWorkflow={this.props.onSaveToWorkflow} filingTypeFlow={this.props.filingTypeFlow}/>;
  };

  buildAutoSubmissionParams = () => {
    const correctionPeriod = buildCorrectionPeriod(this.props.selectedCorrectionPeriod);
    const systemFileName = new URL(this.props.documentUri).pathname.split("/").at(-1) || "";
    // Use filingDocumentFileName if available
    const workflowFileName = this.props.filingDocumentFileName
      ? `${this.props.filingDocumentFileName}.${this.props.documentExtension}`
      : `${this.props.filename}.${this.props.documentExtension}`;

    return {
      companyId: this.props.companyId,
      companyName: this.props.companyName || '',
      companyCode: this.props.companyCode || '',
      countryCode: this.props.countryCode,
      startDate: this.props.fromDate,
      endDate: this.props.toDate,
      frequency: this.props.periodName,
      returnType: ENUMS.getFilingNameForUrlFromFilingTypeId(this.props.filingTypeId),
      returnTypeFlow: this.props.filingTypeFlow || 'Standard',
      userName: this.props.userName || 'Unknown',
      transactionFilter: this.props.returnDisplayType || '0',
      correctionPeriod: correctionPeriod,
      formXmlId: this.props.formXmlId,
      fileName: systemFileName,
      workflowFileName: workflowFileName
    };
  };

  onInitiateAutoSubmission = () => {
    log.debug('onInitiateAutoSubmission() called');

    const autoSubmissionParams = this.buildAutoSubmissionParams();
    log.debug('onInitiateAutoSubmission() - Initiating job with params:', autoSubmissionParams);

    // Disable the button immediately so a user can't fire it twice while the
    // request is in flight; workflowInProgress takes over once it succeeds.
    this.setState({ isInitiating: true });
    this.props.doInitiateAutoSubmission(autoSubmissionParams, (error) => {
      if (error) {
        this.setState({ isInitiating: false });
      }
    });
  };

  checkAutoSubmissionWorkflowInProgress = () => {
    if (!this.props.doCheckWorkflowInProgress) return;

    const isEnabled = isAutoSubmissionEnabled(
      this.props.filingTypeId,
      this.props.countryCode,
      this.props.entitlements,
      this.props.featureFlag
    );
    if (!isEnabled) return;

    // Only the filter fields matter for this check, unlike buildAutoSubmissionParams()
    // which also derives file-name details from documentUri for the initiate call.
    const params = {
      companyId: this.props.companyId,
      countryCode: this.props.countryCode,
      startDate: this.props.fromDate,
      endDate: this.props.toDate,
      frequency: this.props.periodName,
      returnType: ENUMS.getFilingNameForUrlFromFilingTypeId(this.props.filingTypeId)
    };
    log.debug('checkAutoSubmissionWorkflowInProgress() - Checking with params:', params);

    this.props.doCheckWorkflowInProgress(params);
  };

  getInitiateAutoSubmissionButton = () => {
    if (!this.props.doInitiateAutoSubmission) return null;

    const isEnabled = isAutoSubmissionEnabled(
      this.props.filingTypeId,
      this.props.countryCode,
      this.props.entitlements,
      this.props.featureFlag
    );

    if (!isEnabled) return null;

    const hasPermission = canInitiateDirectSubmissionGated(
      this.props.featureFlag?.[FeatureFlagEnum.ENABLE_USER_MANAGEMENT_TILES],
      this.props.directSubmission,
      this.props.companyId,
      this.props.countryCode
    );
    const canInitiate = hasPermission && !this.props.workflowInProgress && !this.state.isInitiating;
    const disabledTooltip = !hasPermission
      ? LANGUAGES_CONTROLLER.format(this.props.languages, 'filingPage_InitiateDirectSubmissionNoPermission')
      : LANGUAGES_CONTROLLER.format(this.props.languages, 'filingPage_InitiateDirectSubmissionInProgress');

    return (
      <ReadOnlyWrapper>
        <button
          className="secondary ml10"
          onClick={this.onInitiateAutoSubmission}
          disabled={!canInitiate}
          title={canInitiate ? undefined : disabledTooltip}
          data-testid="initiate-direct-submission-btn"
        >
          <LanguageStringResolver string='filingPage_InitiateDirectSubmissionBtn' />
        </button>
      </ReadOnlyWrapper>
    );
  };

  onGoToTaskList = () => {
    log.debug('onGoToTaskList() called');
    
    const complianceCalendarUrl = getClientConfig().complianceCalendarUrl;
    
    if (!complianceCalendarUrl) {
      log.warn('onGoToTaskList() - complianceCalendarUrl not configured');
      return;
    }

    const url = `${complianceCalendarUrl}/direct-submission`;
    log.debug('onGoToTaskList() - Navigating to:', url);
    
    window.location.href = url;
  };

  getGoToTaskListButton = () => {
    const isEnabled = isAutoSubmissionEnabled(
      this.props.filingTypeId,
      this.props.countryCode,
      this.props.entitlements,
      this.props.featureFlag
    );
    
    if (!isEnabled) return null;
    
    // Viewing direct submission task/status is not a gated capability - the only Direct
    // Submission grants are Initiate and Approve - so this button stays enabled for
    // read-only users, who can already reach the same page from the Compliance nav menu.
    return (
      <button
        className="secondary ml10"
        onClick={this.onGoToTaskList}
        excludeFromReadonly={true}
        data-testid="direct-submission-status-btn"
      >
        <LanguageStringResolver string='filingPage_DirectSubmissionStatusBtn' />
      </button>
    );
  };

  getApproveButton = (hideBtn) => {
    if (hideBtn) {
      return null;
    }

    return <ApproveFiling onApprove={this.openApprovalModal.bind(this)} />;
  };

  // ESF-978: shared with FilingPage's async flow via FilingHelper.isGbPdfVatReturn
  // so the sync and async Mark as Submitted modals cannot drift apart again.
  isGbPdfVatReturn = () =>
    FilingHelper.isGbPdfVatReturn(this.props.filingTypeId, this.props.countryCode, this.props.documentType);

  getApprovalConfirmationTitle = () =>
    this.isGbPdfVatReturn() ? "filingPage_GbPdfMarkAsSubmittedTitle" : "confirmationModal_TitleApproval";

  getApprovalConfirmationMessage = () => {
    if (this.isGbPdfVatReturn()) {
      return "filingPage_GbPdfMarkAsSubmittedMessage";
    }
    const isLocalListing = this.props.filingTypeId === FilingTypeEnum.LOCALLISTING;
    return isLocalListing ? "local_listing_approval_confirmation" : "approval_confirmation";
  }

  getMtdSubmitFilingButton = (hideBtn) => {
    /*show only if this.props.documentType === FixedFormat and this.props.filename.indexOf(' GB ') > 1*/
    if (hideBtn) {
      return null;
    }

    return (
      <MTDSubmitFiling
        filename={this.props.filename}
        uri={this.props.documentUri}
        vatnumber={this.props.vatnumber}
        companyCode={this.props.companyCode}
        companyId={this.props.companyId}
        onApprove={this.props.onApprove.bind(this)}
      />
    );
  };

  getDESubmitFilingButton = (hideBtn) => {
    /*Show only for DE with report type iVAT transmission*/
    if (hideBtn) {
      return null;
    }

    return (
      <DESubmitFiling
        filename={this.props.filename}
        uri={this.props.documentUri}
        onApprove={this.props.onApprove.bind(this)}
        isVATFiling={DE_VAT_FILE_NAMES.some((x) => this.props.filename.includes(x))}
      />
    );
  };

  getNLSubmitFilingButton = (hideBtn) => {
    if (hideBtn) {
      return null;
    }

    
    const enableNewNLDigiportWindow = this.props.featureFlag?.[FeatureFlagEnum.ENABLE_NEW_NL_DIGIPORT_WINDOW];
    const isVATFiling = NL_VAT_FILE_NAMES.some((x) => this.props.filename.includes(x));

    return enableNewNLDigiportWindow ? (
      <NLSubmissionFilingV2
        filename={this.props.filename}
        uri={this.props.documentUri}
        onApprove={this.props.onApprove.bind(this)}
        isVATFiling={isVATFiling}
        vatnumber={this.props.vatnumber}
        fromDate={this.props.fromDate}
        toDate={this.props.toDate}
        documentExtension={this.props.documentExtension}
        companyName={this.props.companyName}
        periodName={this.props.periodName}
      />
    ) : (
      <NLSubmitFiling
        filename={this.props.filename}
        uri={this.props.documentUri}
        onApprove={this.props.onApprove.bind(this)}
        isVATFiling={isVATFiling}
        vatnumber={this.props.vatnumber}
        fromDate={this.props.fromDate}
        toDate={this.props.toDate}
        documentExtension={this.props.documentExtension}
      />
    );
  };

  getButtonVisibilityFlags = () => {
    const { formName, documentType, filename, periodName } = this.props;
    
    const showDESubmitBtn =
      (formName === DE_VAT_FORM_NAME && DE_VAT_FILE_NAMES.some((x) => filename.includes(x))) ||
      (documentType === XML_FORM_NAME && DE_ECL_FILE_NAMES.some((x) => filename.includes(x))) ||
      (documentType === XML_FORM_NAME && DE_VAT_FILE_NAMES.some((x) => filename.includes(x)) && periodName === PERIOD_YEAR);

    const showNLSubmitBtn =
      documentType === XML_FORM_NAME && (NL_VAT_FILE_NAMES.some((x) => filename.includes(x)) || NL_ECL_FILE_NAMES.some((x) => filename.includes(x)));

    const showMtdSubmitBtn = documentType === GB_FORM_NAME && GB_FILE_NAMES.some((x) => filename.includes(x));

    return {
      showDESubmitBtn,
      showNLSubmitBtn,
      showMtdSubmitBtn,
      hideApprovalBtn: showMtdSubmitBtn || showDESubmitBtn || showNLSubmitBtn
    };
  };

  renderDocumentViewer = () => {
    const fullFileName = `${this.props.filename}.${this.props.documentExtension}`;
    const docType = this.props.documentType;

    // XML documents
    if (this.isXmlDocument()) {
      // HU VAT Return XML only: run HEAD/size check and potentially suppress preview when large.
      if (this.isHuVatReturnXml()) {
        const status = this.state.xmlSizeCheckStatus;
        if (status === null || status === XML_SIZE_CHECK.PENDING) {
          return (
            <div data-test="filing-preview-xml-size-check">
              <BusySpinner busy={true} />
              <span><LanguageStringResolver string="xmlUriViwer_loading" /> {this.props.filename}</span>
            </div>
          );
        }
        if (status === XML_SIZE_CHECK.LARGE) {
          return (
            <div data-test="filing-preview-large-xml" className="mb10">
              <p className="mb5">
                <LanguageStringResolver string="filingPreview_largeXmlMessage" />
              </p>
              <FileSaveAs
                fetchContent={this.onDownloadLargeXml}
                downloadLabel={`Download ${this.props.filename}.${this.props.documentExtension}`}
                filename={this.props.filename}
                mimeType="application/xml"
                isBlob={true}
              />
            </div>
          );
        }
        // SMALL or FALLBACK: show preview as today
      }
      // Non-HU XML (or HU fallback/small): show preview as today with no size check / message.
      return <XmlUriViewer uri={this.props.documentUri} filename={this.props.filename} fileExtension={this.props.documentExtension} />;
    }
    
    // PDF documents
    if (docType === 'PDF' || docType === 'application/pdf') {
      return <PdfUriViewer uri={this.props.documentUri} filename={fullFileName} />;
    }
    
    // Fixed format documents
    if (docType === 'FixedFormat' || docType === 'text/plain' || docType === 'text/csv' || docType === 'application/csv') {
      return <FixedFormatUriViewer uri={this.props.documentUri} filename={this.props.filename} fileExtension={this.props.documentExtension} />;
    }
    
    // Unknown document type
    return (
      <div>
        <LanguageStringResolver string="filingPreviewUnknownType" />: {docType}
      </div>
    );
  };

  renderMultipleFilesMessage = () => {
    return (
      <div>
        <span className="mb5 bold">
          <LanguageStringResolver string="filingPreviewMultipleFiles" />
        </span>
      </div>
    );
  };

  render() {
    log.debug('render(): ', this.props);

    const buttonFlags = this.getButtonVisibilityFlags();
    const showApprovalWarning = FilingHelper.isRawformXmlSupported(this.props.filingTypeId, this.props.countryCode);

    log.debug('render(): showDESubmitBtn/NL/Mtd/Approval', 
      buttonFlags.showDESubmitBtn, 
      buttonFlags.showNLSubmitBtn, 
      buttonFlags.showMtdSubmitBtn, 
      buttonFlags.hideApprovalBtn);

    return (
      <div>
        <div className="clear"></div>
        <div className={withFullStoryExclude()}>
          {this.props.multipleFilesToDownload 
            ? this.renderMultipleFilesMessage() 
            : this.renderDocumentViewer()}
        </div>
        <div className="bottom-nav-panel-sticky">
          <div className="bottom-nav-panel">
            <ApplicationMessagesWrapper messageType="applicationSuccess" excludedMessageIds={this.messageIdsToIgnoreInThisComponent()} />
            <ApplicationMessagesWrapper messageType="applicationError" excludedMessageIds={this.messageIdsToIgnoreInThisComponent()} />
            {this.getApproveButton(buttonFlags.hideApprovalBtn)}
            <ConfirmationModal
              isModalPopupOpen={this.state.isApprovalModalOpen}
              modelTitle={this.getApprovalConfirmationTitle()}
              modelMessage={this.getApprovalConfirmationMessage()}
              onYesClickHandler={this.props.onApprove.bind(this)}
              onCancelClickHandler={this.closeApprovalModal.bind(this)}
            />
            {this.getMtdSubmitFilingButton(!buttonFlags.showMtdSubmitBtn)}
            {this.getDESubmitFilingButton(!buttonFlags.showDESubmitBtn)}
            {this.getNLSubmitFilingButton(!buttonFlags.showNLSubmitBtn)}
            {this.getSaveToWorkflowButton()}
            {this.getInitiateAutoSubmissionButton()}
            {this.getGoToTaskListButton()}
            {showApprovalWarning && <div><LanguageStringResolver string='filingPage_ApproveAndDownloadBtn_FileRenameWarning' /></div>}
          </div>
        </div>
      </div>
    );
  }
  componentDidMount() {
    // Run HEAD/size check ONLY for HU VAT Return XML. All other cases remain unchanged.
    if (this.isHuVatReturnXml()) {
      this.checkXmlSize();
    }
    this.checkAutoSubmissionWorkflowInProgress();
  }

  componentDidUpdate(prevProps) {
    const { documentUri, documentType, filingTypeId, countryCode } = this.props;

    if (!prevProps.entitlements?.loaded && this.props.entitlements?.loaded) {
      this.checkAutoSubmissionWorkflowInProgress();
    }

    const wasApplicable = this.isHuVatReturnXml(prevProps);
    const isApplicable = this.isHuVatReturnXml();
    const relevantChange =
      prevProps.documentUri !== documentUri ||
      prevProps.documentType !== documentType ||
      prevProps.filingTypeId !== filingTypeId ||
      prevProps.countryCode !== countryCode;

    if (!isApplicable) {
      if (wasApplicable) {
        this.setState({ xmlSizeCheckStatus: null });
      }
      return;
    }

    if (relevantChange) {
      this.setState({ xmlSizeCheckStatus: null }, () => this.checkXmlSize());
    }
  }

  UNSAFE_componentWillMount() {
    log.clearDictionaryError(ALL_MESSAGES.FAILED_TO_SAVE_TO_WORKFLOW);
    log.clearSuccessMessage(ALL_MESSAGES.SAVED_TO_WORKFLOW);
  }
}

// Which props do we want to inject, given the global state?
function mapStateToProps(state) {
  log.debug('mapStateToProps: ', state);
  return {
    canUserSaveToWorkflow: state.filing.canUserSaveToWorkflow,
    featureFlag: state.featureFlag?.data,
    entitlements: state.authentication?.entitlements,
    languages: state.languages,
    directSubmission: state.authentication?.data?.iamData?.directSubmission,
    workflowInProgress: state.filing.workflowInProgress
  };
}
function mapDispatchersToProps(dispatch) {
  return {
    doSaveToWorkflow: (params, workflowCallback) => {
      dispatch(FILING_CONTROLLER.doSaveToWorkflow(params, workflowCallback));
    },
    doInitiateAutoSubmission: (params, callback) => {
      dispatch(FILING_CONTROLLER.doInitiateAutoSubmission(params, callback));
    },
    doCheckWorkflowInProgress: (params) => {
      dispatch(FILING_CONTROLLER.checkWorkflowInProgress(params));
    },
  };
}


// Wrap the component to inject dispatch and state into it
export default connect(mapStateToProps, mapDispatchersToProps)(FilingPreview);
